/**
 * Shared chart palette for the Insights page.
 *
 * The three categorical slots are assigned in fixed order and never cycled —
 * slot 1 is always Sessions, slot 2 Leads Captured, slot 3 Orders — so a filter
 * that removes a series never repaints the survivors. A fourth category folds
 * into `other` (neutral) rather than inventing a hue.
 *
 * Both modes were checked with the dataviz palette validator (lightness band,
 * chroma floor, CVD separation, normal-vision floor, contrast vs surface) against
 * their own surface: #ffffff light, #1e293b (secondary-800) dark. The light set
 * sits in the 6–8 CVD floor band, which is why every series that uses it also
 * carries a direct label — identity is never colour alone.
 */

export interface ChartPalette {
  series: [string, string, string];
  other: string;
  accent: string;
  danger: string;
  grid: string;
  axis: string;
  surface: string;
  tooltipBg: string;
  tooltipBorder: string;
  tooltipText: string;
  track: string;
}

const light: ChartPalette = {
  series: ['#007BFF', '#d97706', '#16a34a'],
  other: '#94a3b8',
  accent: '#0891b2',
  danger: '#dc2626',
  grid: '#e2e8f0',
  axis: '#64748b',
  surface: '#ffffff',
  tooltipBg: '#ffffff',
  tooltipBorder: '#e2e8f0',
  tooltipText: '#0f172a',
  track: '#f1f5f9',
};

const dark: ChartPalette = {
  series: ['#3b82f6', '#bd8318', '#2fa86a'],
  other: '#64748b',
  accent: '#22d3ee',
  danger: '#f87171',
  grid: '#334155',
  axis: '#94a3b8',
  surface: '#1e293b',
  tooltipBg: '#1e293b',
  tooltipBorder: '#334155',
  tooltipText: '#f1f5f9',
  track: '#334155',
};

export const getChartPalette = (isDark: boolean): ChartPalette => (isDark ? dark : light);

/** Compact duration for axis ticks and stat tiles: 92s → "1m 32s", 0 → "—". */
export const formatDuration = (seconds: number): string => {
  if (!seconds || seconds < 1) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainder = Math.round(seconds % 60);
  if (minutes < 60) return remainder ? `${minutes}m ${remainder}s` : `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
};

export const formatCompactNumber = (value: number): string =>
  new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(value);
